import { useTranslation } from 'react-i18next'
import { router } from '@inertiajs/react'
import { OrganizationLayout } from '../../layouts/organization-layout'
import { getDateLocale } from '@/lib/i18n'
import { Button } from '../../components/ui/button'
import { Badge } from '../../components/ui/badge'
import { Separator } from '../../components/ui/separator'
import { ArrowLeftIcon, CheckCircle2, XCircle, Clock } from 'lucide-react'

interface Organization {
  id: number
  name: string
}

interface Purchase {
  id: number
  credits_amount: number
  price_usd: number
  status: string
  stripe_payment_intent_id: string | null
  created_at: string
}

interface PurchaseHistoryProps {
  organization: Organization
  purchases: Purchase[]
}

function PurchaseHistory({ organization, purchases }: PurchaseHistoryProps) {
  const { t } = useTranslation('organizations')

  const formatDate = (isoString: string) => {
    const date = new Date(isoString)
    return date.toLocaleDateString(getDateLocale(), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <Badge variant="default" className="bg-green-100 text-green-800 hover:bg-green-200">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            {t('purchaseHistory.status.completed')}
          </Badge>
        )
      case 'failed':
        return (
          <Badge variant="destructive">
            <XCircle className="h-3 w-3 mr-1" />
            {t('purchaseHistory.status.failed')}
          </Badge>
        )
      case 'pending':
        return (
          <Badge variant="secondary">
            <Clock className="h-3 w-3 mr-1" />
            {t('purchaseHistory.status.pending')}
          </Badge>
        )
      default:
        return <Badge variant="secondary">{status}</Badge>
    }
  }

  const completedPurchases = purchases.filter(p => p.status === 'completed')
  const totalCredits = completedPurchases.reduce((sum, p) => sum + p.credits_amount, 0)
  const totalSpent = completedPurchases.reduce((sum, p) => sum + p.price_usd, 0)

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col">
        <div className="px-4 py-6 lg:px-6">
          <div className="mx-auto max-w-4xl space-y-8">

            {/* Back link */}
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-muted-foreground"
              onClick={() => router.visit('/organization/subscription')}
            >
              <ArrowLeftIcon className="size-4" />
              {t('purchaseHistory.backButton')}
            </Button>

            {/* Summary section */}
            {purchases.length > 0 && (
              <>
                <div>
                  <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('purchaseHistory.summary.title', { defaultValue: 'Summary' })}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t('purchaseHistory.pageDescription', { organizationName: organization.name })}
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className="rounded-lg border p-4">
                    <p className="text-xs font-medium text-muted-foreground">{t('purchaseHistory.summary.totalPurchases')}</p>
                    <p className="mt-1 text-2xl font-semibold">{purchases.length}</p>
                  </div>
                  <div className="rounded-lg border p-4">
                    <p className="text-xs font-medium text-muted-foreground">{t('purchaseHistory.summary.totalCreditsPurchased')}</p>
                    <p className="mt-1 text-2xl font-semibold">{totalCredits}</p>
                  </div>
                  <div className="rounded-lg border p-4">
                    <p className="text-xs font-medium text-muted-foreground">{t('purchaseHistory.summary.totalAmountSpent')}</p>
                    <p className="mt-1 text-2xl font-semibold">${totalSpent.toFixed(2)}</p>
                  </div>
                </div>

                <Separator />
              </>
            )}

            {/* Purchase History Table */}
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('purchaseHistory.table.date')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('purchaseHistory.table.credits')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('purchaseHistory.table.amount')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('purchaseHistory.table.status')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('purchaseHistory.table.transactionId')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {purchases.length > 0 ? (
                    purchases.map((purchase) => (
                      <tr key={purchase.id} className="hover:bg-muted/50 transition-colors">
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          {formatDate(purchase.created_at)}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold">
                          {purchase.credits_amount}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold">
                          ${purchase.price_usd.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {getStatusBadge(purchase.status)}
                        </td>
                        <td className="px-4 py-3">
                          {purchase.stripe_payment_intent_id ? (
                            <code className="text-xs bg-muted px-2 py-1 rounded">
                              {purchase.stripe_payment_intent_id.slice(0, 20)}...
                            </code>
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-muted-foreground">
                        <p>{t('purchaseHistory.empty.message')}</p>
                        <Button
                          variant="outline"
                          onClick={() => router.visit('/organization/subscription')}
                          className="mt-4"
                        >
                          {t('purchaseHistory.empty.purchaseButton')}
                        </Button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

          </div>
        </div>
      </div>
    </div>
  )
}

PurchaseHistory.layout = (page: React.ReactNode) => <OrganizationLayout>{page}</OrganizationLayout>

export default PurchaseHistory
